/**
 * The zoom math and state transitions behind the screen-share "100%" (actual
 * size) toggle. Pulled out of the components so it is unit-testable without
 * mounting anything or mocking ResizeObserver — the only thing that varies
 * between a render and a test is a handful of numbers and an action.
 */

export interface FitBox {
  /** Viewport size in CSS px, scrollbars included — ZoomPanVideo's own `box`. */
  boxWidth: number
  boxHeight: number
  /** The video's own decoded size, in device pixels (track.getSettings() /
   *  videoWidth·videoHeight). */
  videoWidth: number
  videoHeight: number
  devicePixelRatio: number
}

export interface ZoomMetrics {
  /** CSS px size the frame renders at when zoom === 1 ("Fit"). */
  fitWidth: number
  fitHeight: number
  /** zoom value that renders the frame at its real, 1:1 device-pixel size. */
  naturalZoom: number
  /** Whether offering "100%" would show the viewer anything Fit does not —
   *  false once the source is already at or below the tile's own size, where
   *  Fit and 100% are the identical frame and a toggle between them would be
   *  a button that does nothing. */
  canViewAtNaturalSize: boolean
}

/** Below this, "100%" would not render visibly differently from Fit. */
const MEANINGFUL_ZOOM_DELTA = 1.02

/** Rendered frame size at zoom=1 — the same math ZoomPanVideo lays out with,
 *  kept in one place so the toggle's math can never drift from what it
 *  toggles. */
function fitSize(boxWidth: number, boxHeight: number, aspect: number): { width: number; height: number } {
  if (!aspect || !boxWidth || !boxHeight) return { width: boxWidth, height: boxHeight }
  return boxWidth / boxHeight > aspect
    ? { width: boxHeight * aspect, height: boxHeight }
    : { width: boxWidth, height: boxWidth / aspect }
}

export function computeZoomMetrics(box: FitBox): ZoomMetrics {
  const { boxWidth, boxHeight, videoWidth, videoHeight, devicePixelRatio } = box
  const aspect = videoHeight ? videoWidth / videoHeight : 0
  const { width: fitWidth, height: fitHeight } = fitSize(boxWidth, boxHeight, aspect)
  const dpr = devicePixelRatio || 1
  const naturalWidth = videoWidth / dpr
  const naturalZoom = fitWidth > 0 ? naturalWidth / fitWidth : 1
  return {
    fitWidth,
    fitHeight,
    naturalZoom,
    canViewAtNaturalSize: Number.isFinite(naturalZoom) && naturalZoom > MEANINGFUL_ZOOM_DELTA,
  }
}

export interface ZoomUiState {
  zoomLevel: number
  /** True while the viewer is pinned to "100%", as opposed to having merely
   *  zoomed to a level that happens to match it once. Distinguishing the two
   *  is what lets a presenter's mid-share resize keep 100% exactly 100%
   *  (see `metricsUpdated` below) without also hijacking a manual zoom a
   *  viewer chose themselves. */
  isAtNaturalSize: boolean
}

export const FIT_STATE: ZoomUiState = { zoomLevel: 1, isAtNaturalSize: false }

const ZOOM_STEP = 0.25
const ZOOM_MIN = 0.5
const ZOOM_MAX = 3

export type ZoomAction =
  | { type: 'zoomIn' }
  | { type: 'zoomOut' }
  | { type: 'resetToFit' }
  /** The viewer clicked "100%". */
  | { type: 'viewAtNaturalSize'; naturalZoom: number }
  /** The source's own resolution changed (the presenter resized their
   *  window/display mid-share) — ZoomPanVideo's `resize` listener. */
  | { type: 'metricsUpdated'; naturalZoom: number }
  /** A different share became active, or this one restarted. Always Fit:
   *  a zoom level chosen for the last presenter's resolution is a stale
   *  framing for a different capture, pinned-100% or not. */
  | { type: 'shareChanged' }

export function zoomReducer(state: ZoomUiState, action: ZoomAction): ZoomUiState {
  switch (action.type) {
    case 'zoomIn':
      return { zoomLevel: Math.min(state.zoomLevel + ZOOM_STEP, ZOOM_MAX), isAtNaturalSize: false }
    case 'zoomOut':
      return { zoomLevel: Math.max(state.zoomLevel - ZOOM_STEP, ZOOM_MIN), isAtNaturalSize: false }
    case 'resetToFit':
    case 'shareChanged':
      return FIT_STATE
    case 'viewAtNaturalSize':
      return { zoomLevel: action.naturalZoom, isAtNaturalSize: true }
    case 'metricsUpdated':
      return state.isAtNaturalSize ? { zoomLevel: action.naturalZoom, isAtNaturalSize: true } : state
    default:
      return state
  }
}
